import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, KeyRound, Mail } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { queryClient } from '@/config/query-client'
import { requestPasswordReset, resetPassword } from '@/services/api/auth.service'
import { ApiError } from '@/services/api/client'
import { useAuthStore } from '@/stores/auth.store'
import { usePermissionStore } from '@/stores/permission.store'
import { useSubscriptionStore } from '@/stores/subscription.store'
import { useWorkspaceStore } from '@/stores/workspace.store'
import {
  forgotPasswordRequestSchema, resetPasswordRequestSchema,
  type ForgotPasswordRequest, type ResetPasswordRequest,
} from '@/types/api/auth'

function RecoveryCard({ title, description, children }: { title: string; description: string; children: ReactNode }) {
  useEffect(() => {
    const meta = document.createElement('meta')
    meta.name = 'referrer'
    meta.content = 'no-referrer'
    document.head.appendChild(meta)
    return () => meta.remove()
  }, [])
  return (
    <main className="flex min-h-screen items-center justify-center bg-neutral-50 px-4 py-8">
      <div className="w-full max-w-md space-y-5">
        <div className="text-center"><KeyRound className="mx-auto mb-3 h-9 w-9 text-primary-600" /><p className="font-semibold">Generic WorkFlow</p></div>
        <Card>
          <CardHeader><CardTitle className="text-2xl">{title}</CardTitle><CardDescription>{description}</CardDescription></CardHeader>
          <CardContent>{children}</CardContent>
        </Card>
        <Link to="/login" className="flex items-center justify-center gap-2 text-sm text-primary-600 hover:underline"><ArrowLeft className="h-4 w-4" />Back to sign in</Link>
      </div>
    </main>
  )
}

function recoveryError(error: unknown) {
  if (error instanceof ApiError && error.status === 429) return 'Too many attempts. Please wait a minute and try again.'
  if (error instanceof ApiError && error.status < 500) return error.detail
  return 'We could not reach password recovery. Please try again shortly.'
}

export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<ForgotPasswordRequest>({
    resolver: zodResolver(forgotPasswordRequestSchema),
  })

  async function submit(data: ForgotPasswordRequest) {
    setError('')
    try { await requestPasswordReset(data); setSent(true) }
    catch (failure) { setError(recoveryError(failure)) }
  }

  return (
    <RecoveryCard title={sent ? 'Check your email' : 'Forgot your password?'} description={sent
      ? 'If an account exists for that email, you will receive a password reset link shortly.'
      : 'Enter your account email and we’ll send you a link to reset your password.'}>
      {sent ? <div role="status" className="space-y-4 text-sm text-neutral-600">
        <Mail className="h-7 w-7 text-primary-600" />
        <p>The link is valid for 10 minutes. Check your spam folder too.</p>
        <Button variant="outline" className="w-full" onClick={() => setSent(false)}>Try another email or request a new link</Button>
      </div> : <form onSubmit={handleSubmit(submit)} className="space-y-4" noValidate>
        <div className="space-y-2"><Label htmlFor="recovery-email">Email address</Label>
          <Input id="recovery-email" type="email" autoComplete="email" maxLength={254} {...register('email')} aria-invalid={!!errors.email} aria-describedby={errors.email ? 'recovery-email-error' : undefined} />
          {errors.email && <p id="recovery-email-error" className="text-sm text-destructive-600">{errors.email.message}</p>}
        </div>
        {error && <p role="alert" className="text-sm text-destructive-600">{error}</p>}
        <Button className="w-full" type="submit" disabled={isSubmitting}>{isSubmitting ? 'Sending…' : 'Send reset link'}</Button>
      </form>}
    </RecoveryCard>
  )
}

export function ResetPasswordPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const token = new URLSearchParams(location.hash.slice(1)).get('token') ?? ''
  const validToken = /^[A-Za-z0-9_-]{43}$/.test(token)
  const [done, setDone] = useState(false)
  const [error, setError] = useState('')
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<ResetPasswordRequest>({
    resolver: zodResolver(resetPasswordRequestSchema), values: { token, password: '', confirmPassword: '' },
  })

  async function submit(data: ResetPasswordRequest) {
    setError('')
    try {
      await resetPassword(data)
      reset()
      useAuthStore.getState().reset()
      usePermissionStore.getState().clear()
      useSubscriptionStore.getState().clear()
      useWorkspaceStore.getState().clearWorkspace()
      queryClient.clear()
      setDone(true)
      navigate('/reset-password', { replace: true })
    } catch (failure) { setError(recoveryError(failure)) }
  }

  return (
    <RecoveryCard title={done ? 'Password updated' : 'Set a new password'} description={done
      ? 'Your previous sessions have ended. Sign in with your new password.' : 'Choose a password with at least 8 characters.'}>
      {done ? <div role="status"><Link className="text-primary-600 hover:underline" to="/login">Sign in with your new password</Link></div>
        : !validToken ? <div role="alert" className="space-y-4 text-sm"><p>This reset link is missing or invalid.</p><Link className="text-primary-600 hover:underline" to="/forgot-password">Request a new reset link</Link></div>
          : <form className="space-y-4" onSubmit={handleSubmit(submit)} noValidate>
            <div className="space-y-2"><Label htmlFor="new-password">New password</Label>
              <Input id="new-password" type="password" autoComplete="new-password" maxLength={72} {...register('password')} aria-invalid={!!errors.password} aria-describedby={errors.password ? 'new-password-error' : undefined} />
              {errors.password && <p id="new-password-error" className="text-sm text-destructive-600">{errors.password.message}</p>}
            </div>
            <div className="space-y-2"><Label htmlFor="confirm-password">Confirm new password</Label>
              <Input id="confirm-password" type="password" autoComplete="new-password" maxLength={72} {...register('confirmPassword')} aria-invalid={!!errors.confirmPassword} aria-describedby={errors.confirmPassword ? 'confirm-password-error' : undefined} />
              {errors.confirmPassword && <p id="confirm-password-error" className="text-sm text-destructive-600">{errors.confirmPassword.message}</p>}
            </div>
            {error && <div role="alert" className="space-y-2 text-sm text-destructive-600"><p>{error}</p><Link to="/forgot-password" className="text-primary-600 hover:underline">Request a new reset link</Link></div>}
            <Button type="submit" className="w-full" disabled={isSubmitting}>{isSubmitting ? 'Updating…' : 'Reset password'}</Button>
          </form>}
    </RecoveryCard>
  )
}
